import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

/**
 * `fonte` é o material que o gabinete ensina ao OTO. O arquivo fica guardado e
 * disponível para download; o conteúdo não é lido por nada no sistema.
 *
 * As rotas de anexo vivem sob `/gab/anexos` e exigem o módulo `gab`
 * (`EntitlementGuard`). Um mandato que tenha OTO sem Gabinete360 recebe 402 ao
 * anexar na tela de Perfil. Duplicar as quatro rotas sob `oto` para cobrir esse
 * caso significaria duas cópias do limite de tamanho, da lista de MIMEs e da
 * verificação de posse — o preço é maior que o caso.
 */
export type DonoAnexo = 'solicitacao' | 'contato' | 'visita' | 'fonte';

export interface Anexo {
  id: string;
  dono_tipo: DonoAnexo;
  dono_id: string;
  tipo: string;
  nome: string;
  mime: string;
  tamanho: number;
  sha256: string;
  created_at: string;
}

export const TIPOS_ANEXO = [
  'Pesquisa',
  'Notícia / jornal',
  'Rede social (export)',
  'Plano / programa',
  'Discurso / entrevista',
  'Relatório público',
  'Prova de consentimento',
  'Outro',
];

/** Mesmo teto do servidor, para recusar antes de subir o arquivo à toa. */
export const LIMITE_ANEXO_BYTES = 3 * 1024 * 1024;

/** Acima disto, imagem é comprimida antes de enviar. */
const ALVO_COMPRESSAO_BYTES = 1.5 * 1024 * 1024;
const LADO_MAXIMO = 2000;

export function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

function lerComoDataUrl(arquivo: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result));
    leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/**
 * Reduz a imagem antes do envio.
 *
 * Foto de celular passa fácil de 5 MB, e o limite do servidor é 3 MB. Sem isto,
 * o caso mais comum de uso — fotografar um buraco na rua e anexar — falharia
 * quase sempre. Redimensiona para no máximo 2000px no maior lado e recodifica
 * em JPEG, o que resolve a esmagadora maioria dos casos sem prejudicar a
 * leitura do que a foto documenta.
 *
 * Só age em imagem: PDF e áudio não podem ser recomprimidos sem perder o que
 * são, e para eles a recusa por tamanho é a resposta honesta.
 */
async function comprimirSePreciso(arquivo: File): Promise<{ blob: Blob; mime: string }> {
  const ehImagem = arquivo.type.startsWith('image/');
  if (!ehImagem || arquivo.size <= ALVO_COMPRESSAO_BYTES) {
    return { blob: arquivo, mime: arquivo.type };
  }

  try {
    const dataUrl = await lerComoDataUrl(arquivo);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Imagem ilegível.'));
      el.src = dataUrl;
    });

    const escala = Math.min(1, LADO_MAXIMO / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * escala);
    canvas.height = Math.round(img.height * escala);

    const ctx = canvas.getContext('2d');
    if (!ctx) return { blob: arquivo, mime: arquivo.type };
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.82),
    );

    // Se a recompressão não ajudou, o original vale mais: comprimir para ficar
    // maior seria perder qualidade sem ganhar nada.
    if (!blob || blob.size >= arquivo.size) return { blob: arquivo, mime: arquivo.type };
    return { blob, mime: 'image/jpeg' };
  } catch {
    // Falha na compressão não impede o envio; se estourar o limite, o servidor
    // recusa com a mensagem que diz o tamanho real.
    return { blob: arquivo, mime: arquivo.type };
  }
}

function paraBase64(blob: Blob): Promise<string> {
  return lerComoDataUrl(blob).then((url) => url.split(',')[1] ?? '');
}

export const anexosApi = {
  list: async (donoTipo: DonoAnexo, donoId: string, token: string) => {
    const res = await axios.get<Anexo[]>(
      `${API_URL}/gab/anexos/${donoTipo}/${donoId}`,
      auth(token),
    );
    return res.data;
  },

  /**
   * `rota` troca só o endereço do POST, nunca o corpo.
   *
   * A evidência da visita sai por `gab/agenda-interna/:id/evidencias`, que
   * aceita o `articulador` — o papel que faz o check-in geolocalizado e que a
   * rota genérica de anexos não inclui. Do outro lado, essa rota chama o mesmo
   * `AnexosService.criar`, então limite de tamanho, lista de MIMEs e
   * verificação de posse continuam num lugar só, aqui e lá.
   */
  upload: async (
    donoTipo: DonoAnexo,
    donoId: string,
    arquivo: File,
    tipo: string,
    token: string,
    opcoes?: { rota?: string },
  ) => {
    const { blob, mime } = await comprimirSePreciso(arquivo);

    if (blob.size > LIMITE_ANEXO_BYTES) {
      throw new Error(
        `"${arquivo.name}" tem ${formatarTamanho(blob.size)} e o limite é ` +
          `${formatarTamanho(LIMITE_ANEXO_BYTES)}.`,
      );
    }

    const res = await axios.post<Anexo>(
      `${API_URL}${opcoes?.rota ?? `/gab/anexos/${donoTipo}/${donoId}`}`,
      {
        nome: arquivo.name,
        mime: mime || 'application/octet-stream',
        tipo,
        conteudo_base64: await paraBase64(blob),
      },
      auth(token),
    );
    return res.data;
  },

  /**
   * Baixa pelo axios, e não por um link direto: a rota exige o cabeçalho de
   * autenticação, que um `<a href>` não tem como enviar.
   */
  baixar: async (anexo: Anexo, token: string) => {
    const res = await axios.get(`${API_URL}/gab/anexos/${anexo.id}/conteudo`, {
      ...auth(token),
      responseType: 'blob',
    });

    const url = URL.createObjectURL(res.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = anexo.nome;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  remover: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/gab/anexos/${id}`, auth(token));
  },
};
